import React from 'react';
import * as dashboardApi from '../../api/dashboardApi';
import PdfViewer from '../../components/PdfViewer/PdfViewer';
import styles from './RegulationDetailModal.module.css';

const FORMAT_SUFFIX_MAP: Record<string, string> = {
    TXT: 'Text Document', PDF: 'PDF Document', HTML: 'Web Document',
    XML: 'XML Document', DOC: 'Word Document',
};
const formatDocType = (type?: string): string => {
    if (!type) return 'N/A';
    const parenMatch = type.match(/\(([^)]+)\)/);
    const base = type.replace(/\s*\([^)]*\)/g, '').trim();
    if (parenMatch && parenMatch[1].length > base.length) return parenMatch[1];
    const parts = base.split('_');
    const last = parts[parts.length - 1].toUpperCase();
    if (FORMAT_SUFFIX_MAP[last] && parts.length > 1) return FORMAT_SUFFIX_MAP[last];
    return base.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

interface RegulationDetailModalProps {
  regulation: dashboardApi.RegulationSummary;
  onClose: () => void;
  isLoading?: boolean;
}

export const RegulationDetailModal: React.FC<RegulationDetailModalProps> = ({ regulation, onClose, isLoading }) => {
  const normalizedFormat =
    regulation.content_format?.toLowerCase() || regulation.content_type?.toLowerCase() || '';
  const trimmedContent = regulation.content?.trim() || '';
  const lowerContent = trimmedContent.toLowerCase();

  const sourceLooksPdf = Boolean(
    regulation.source_url &&
      (regulation.source_url.toLowerCase().endsWith('.pdf') ||
        regulation.source_url.toLowerCase().includes('format=pdf'))
  );
  const payloadLooksHtml = ['<!doctype', '<html', '<body', '<div', '<p', '<table'].some((token) =>
    lowerContent.startsWith(token)
  );
  const payloadLooksPdf = trimmedContent.startsWith('%PDF-');

  const isPdfContent =
    sourceLooksPdf ||
    (!!trimmedContent && (normalizedFormat.includes('pdf') || payloadLooksPdf));

  const isHtmlContent =
    !isPdfContent &&
    !!trimmedContent &&
    (normalizedFormat.includes('html') || payloadLooksHtml);

  const inlinePdfPayload = isPdfContent && payloadLooksPdf ? regulation.content || null : null;

  const convertPlainTextToHtml = (text: string) => {
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const safe = escapeHtml(text);
    return `<p>${safe
      .replace(/\r\n/g, '\n')
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.replace(/\n/g, '<br />'))
      .join('</p><p>')}</p>`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'enacted': return 'var(--ds-success)';
      case 'proposed': return 'var(--ds-warning)';
      case 'draft': return 'var(--ds-text-muted)';
      default: return 'var(--primary-500)';
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner} aria-live="polite">
              Loading details...
            </div>
          </div>
        )}
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>{regulation.title}</h2>
            <p className={styles.modalSubtitle}>
              {regulation.jurisdiction_name} · {formatDocType(regulation.regulation_type)}
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Status Badge */}
          {regulation.status && (
            <div className={styles.section}>
              <span 
                className={styles.statusBadge}
                style={{ backgroundColor: getStatusColor(regulation.status) }}
              >
                {regulation.status}
              </span>
            </div>
          )}

          {/* Key Dates */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Key Dates</h3>
            <div className={styles.dateGrid}>
              {regulation.last_action_date && (
                <div className={styles.dateItem}>
                  <span className={styles.dateLabel}>Last Action Date</span>
                  <span className={styles.dateValue}>{formatDate(regulation.last_action_date)}</span>
                </div>
              )}
              {regulation.effective_date && (
                <div className={styles.dateItem}>
                  <span className={styles.dateLabel}>Effective Date</span>
                  <span className={styles.dateValue}>{formatDate(regulation.effective_date)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Regulation Details */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Details</h3>
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Jurisdiction</span>
                <span className={styles.detailValue}>{regulation.jurisdiction_name} ({regulation.jurisdiction_code})</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Type</span>
                <span className={styles.detailValue}>{formatDocType(regulation.regulation_type)}</span>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Summary</h3>
            <p className={styles.summaryText}>
              {regulation.summary || 'Not available'}
            </p>
          </div>

          {regulation.content && (
            <div className={styles.section}>
              <div className={styles.fullTextHeader}>
                <h3 className={styles.sectionTitle}>Full Text</h3>
                {(regulation.content_type || regulation.content_format) && (
                  <span className={styles.textMeta}>
                    {regulation.content_type || regulation.content_format}
                  </span>
                )}
              </div>
              <div className={styles.fullTextBlock}>
                {isHtmlContent ? (
                  <iframe
                    title="Regulation HTML"
                    srcDoc={regulation.content}
                    sandbox="allow-same-origin allow-popups"
                    className={styles.fullTextIframe}
                  />
                ) : isPdfContent ? (
                  <PdfViewer
                    content={inlinePdfPayload}
                    sourceUrl={regulation.source_url || null}
                    height={520}
                    className={styles.fullTextPdf}
                  />
                ) : (
                  <div
                    className={styles.fullTextHtml}
                    dangerouslySetInnerHTML={{
                      __html: convertPlainTextToHtml(regulation.content || ''),
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Source Link */}
          {regulation.source_url && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Source</h3>
              <a 
                href={regulation.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.sourceLink}
              >
                View Original Document →
              </a>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.closeButtonSecondary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
